(() => {
  'use strict';

  const PUBLIC_ENABLED = true; // __LB_PUBLIC_ENABLED__
  const VERSION = '2026-10-27-welcome-cadence';
  const PRIVATE_TEST_TOKEN = 'ladyboss-popup-20260717-sapphire';
  const WELCOME_FORMS = Object.freeze({ desktop: 'SDDjsT', mobile: 'SG2jCj' });
  const RESCUE_FORMS = Object.freeze({ desktop: 'ScM7UP', mobile: 'XK637B' });
  const WELCOME_FORM_IDS = new Set(Object.values(WELCOME_FORMS));
  const RESCUE_FORM_IDS = new Set(Object.values(RESCUE_FORMS));
  const WELCOME_STORAGE_KEY = 'ladyboss:welcome-popup:v1';
  const RESCUE_STORAGE_KEY = 'ladyboss:rescue-popup:v1';
  const RESCUE_SESSION_KEY = 'ladyboss:rescue-popup:visit:v1';
  const POPUP_VISIT_KEY = 'ladyboss:popup:visit-choice:v1';
  const LOCK_KEY = 'ladyboss:welcome-popup:lock:v1';
  const LOCK_TTL_MS = 20 * 60 * 1000;
  const PERMANENT_UNTIL = 8_640_000_000_000_000;
  const RESCUE_COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000;
  const RESCUE_HEADSTART_MS = 2_500;
  const EXCLUDED_PATH = /^\/(?:cart|checkout|checkouts|account)(?:\/|$)/i;
  const OWNED_MEDIUM = /^(?:email|sms)$/i;
  const MOBILE_QUERY = '(max-width: 767px)';
  const RESCUE_PROFILE_PROPERTIES = Object.freeze({
    completed: 'LadyBoss Rescue Popup Completed',
    permanentDismissal: 'LadyBoss Rescue Popup Permanently Dismissed',
  });
  const RESCUE_METRICS = Object.freeze({
    completed: 'LadyBoss Rescue Completed',
    permanentDismissal: 'LadyBoss Rescue Permanent Dismissal',
  });
  const RULES = Object.freeze({
    desktop: Object.freeze({ delayMs: 8_000, scrollPercent: 8, scrollCapPx: 120 }),
    mobile: Object.freeze({ delayMs: 6_000, scrollPercent: 5, scrollCapPx: 80 }),
  });
  const DISMISSAL_COOLDOWNS = Object.freeze([
    7 * 60 * 1000,
    7 * 60 * 1000,
    30 * 24 * 60 * 60 * 1000,
    90 * 24 * 60 * 60 * 1000,
    PERMANENT_UNTIL,
  ]);

  const runtime = {
    activeFormId: null,
    cartConfirmedEmpty: false,
    elapsed: false,
    exitSignal: false,
    freshEngagement: false,
    freshEngagementAt: 0,
    initialized: false,
    opened: false,
    opening: false,
    rescueSeenOnVisit: false,
    scrollQualified: false,
    lastScrollY: window.scrollY,
    mobilePeakY: window.scrollY,
    mobilePeakAt: performance.now(),
    retryCount: 0,
    welcomeFallbackTimer: null,
    welcomeListenersStarted: false,
    welcomeCartCheck: false,
    welcomeCartRevision: 0,
    pageStartedAt: Date.now(),
    tabId:
      typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };

  const query = new URLSearchParams(window.location.search);
  const previewMode = query.get('lb_popup_preview') === PRIVATE_TEST_TOKEN;
  const previewResetOnly = previewMode && query.get('lb_popup_reset_only') === '1';

  function defaultWelcomeState() {
    return {
      completed: false,
      completedMetricQueued: false,
      dismissals: 0,
      dismissalPolicyVersion: 2,
      dismissalStep: 0,
      dismissalsAtStep: 0,
      emailCaptured: false,
      lastCloseAt: 0,
      lastFormId: null,
      lastPath: null,
      permanent: false,
      suppressedUntil: 0,
      version: 1,
    };
  }

  function defaultRescueState() {
    return {
      completed: false,
      dismissals: 0,
      lastCloseAt: 0,
      lastFormId: null,
      permanent: false,
      permanentDismissed: false,
      permanentDismissalMetricQueued: false,
      suppressedUntil: 0,
      version: 1,
    };
  }

  function defaultRescueVisitState() {
    return { closed: false, opened: false, submitted: false, version: 1 };
  }

  function storageWorks() {
    try {
      const probe = `${WELCOME_STORAGE_KEY}:probe`;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  }

  function readLocalJson(key, fallback) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  function readSessionJson(key, fallback) {
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  function readWelcomeState() {
    const saved = readLocalJson(WELCOME_STORAGE_KEY, {});
    const state = { ...defaultWelcomeState(), ...saved };
    const count = Math.max(0, Math.min(5, Math.floor(Number(state.dismissals) || 0)));
    if (saved.dismissalPolicyVersion !== 2) {
      // Legacy second/third/fourth closes already earned 30d/90d/permanent.
      // Preserve those stages and their exact expiry; only the first stage
      // gains the newly approved additional seven-minute opportunity.
      state.dismissalStep = count >= 2 ? Math.min(5, count + 1) : count;
    } else {
      // An already-open older tab can still write a later dismissal. Never
      // move its history backward or shorten the suppression it recorded.
      const extraCloses = Math.max(0, count - Number(saved.dismissalsAtStep || 0));
      state.dismissalStep = Math.min(5, Number(saved.dismissalStep || 0) + extraCloses);
      if (extraCloses && count >= 2) state.dismissalStep = Math.max(state.dismissalStep, Math.min(5, count + 1));
    }
    state.dismissals = count;
    state.dismissalPolicyVersion = 2;
    state.dismissalsAtStep = count;
    return state;
  }

  function writeWelcomeState(next) {
    try {
      window.localStorage.setItem(WELCOME_STORAGE_KEY, JSON.stringify(next));
      return true;
    } catch {
      return false;
    }
  }

  function readRescueState() {
    return { ...defaultRescueState(), ...readLocalJson(RESCUE_STORAGE_KEY, {}) };
  }

  function writeRescueState(next) {
    try {
      window.localStorage.setItem(RESCUE_STORAGE_KEY, JSON.stringify(next));
      return true;
    } catch {
      return false;
    }
  }

  function readRescueVisitState() {
    return { ...defaultRescueVisitState(), ...readSessionJson(RESCUE_SESSION_KEY, {}) };
  }

  function writeRescueVisitState(patch) {
    const next = { ...readRescueVisitState(), ...patch };
    try {
      window.sessionStorage.setItem(RESCUE_SESSION_KEY, JSON.stringify(next));
      return true;
    } catch {
      return false;
    }
  }

  function currentDevice() {
    return window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop';
  }

  function currentWelcomeFormId() {
    return WELCOME_FORMS[currentDevice()];
  }

  function isExcludedVisit() {
    if (EXCLUDED_PATH.test(window.location.pathname)) return true;
    const medium = new URLSearchParams(window.location.search).get('utm_medium');
    return Boolean(medium && OWNED_MEDIUM.test(medium.trim()));
  }

  function validExchange(value) {
    return typeof value === 'string' && value.length <= 2048 && /^[A-Za-z0-9_-]{12,}\.WfN9e3$/.test(value);
  }

  function welcomeAudienceBlocked() {
    try {
      if (validExchange(query.get('_kx'))) {
        window.sessionStorage.setItem('ladyboss:popup:owned-visit:v1', '1');
        return true;
      }
      if (window.sessionStorage.getItem(POPUP_VISIT_KEY) === 'spinner') return true;
      if (window.sessionStorage.getItem('ladyboss:popup:known-profile:v1') === '1') return true;
      if (window.sessionStorage.getItem('ladyboss:popup:owned-visit:v1') === '1') return true;
      const replacement = window.__ladybossSpinnerReplacement;
      if (typeof replacement?.blocksWelcome === 'function') return replacement.blocksWelcome();
      // A recognized cookie must not fall through to acquisition if the
      // coordinator failed to load. An anonymous cookie alone is not identity.
      const raw = document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith('__kla_id='));
      if (!raw) return false;
      const cookie = JSON.parse(window.atob(decodeURIComponent(raw.slice('__kla_id='.length))));
      return validExchange(cookie?.$exchange_id);
    } catch {
      return true;
    }
  }

  function commitWelcomeVisit() {
    try {
      if (window.sessionStorage.getItem(POPUP_VISIT_KEY) === 'spinner') return false;
      window.sessionStorage.setItem(POPUP_VISIT_KEY, 'welcome');
      return true;
    } catch {
      return false;
    }
  }

  function isExcludedSpinnerVisit() {
    return EXCLUDED_PATH.test(window.location.pathname);
  }

  function welcomeSuppressionActive(state, now = Date.now()) {
    return state.completed || state.permanent || Number(state.suppressedUntil || 0) > now;
  }

  function rescueSuppressionActive(state, now = Date.now()) {
    return state.completed || state.permanent || Number(state.suppressedUntil || 0) > now;
  }

  function rescueVisitHandled() {
    if (runtime.rescueSeenOnVisit) return true;
    const visit = readRescueVisitState();
    return visit.opened || visit.closed || visit.submitted;
  }

  function acquireLock(formId) {
    const now = Date.now();
    const lock = readLocalJson(LOCK_KEY, null);
    if (lock && lock.expiresAt > now && lock.owner !== runtime.tabId) return false;
    try {
      window.localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ formId, owner: runtime.tabId, expiresAt: now + LOCK_TTL_MS, version: VERSION }),
      );
      return true;
    } catch {
      return false;
    }
  }

  function releaseLock() {
    const lock = readLocalJson(LOCK_KEY, null);
    if (!lock || lock.owner === runtime.tabId || lock.expiresAt <= Date.now()) {
      try {
        window.localStorage.removeItem(LOCK_KEY);
      } catch {
        // Storage failures are handled by the fail-closed eligibility check.
      }
    }
  }

  function hasVisibleDialog() {
    return Array.from(document.querySelectorAll('[role="dialog"]')).some((element) => {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    });
  }

  function klaviyoConfirmed() {
    if (window._klOnsite) return true;
    return Array.from(document.scripts).some((script) => /klaviyo|onsite/i.test(script.src || ''));
  }

  function queueLegacyIdentify(properties) {
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', properties]);
    } catch {
      // A future page load retries durable rescue properties from local state.
    }
  }

  function identifyProfile(properties) {
    try {
      if (typeof window.klaviyo?.identify === 'function') {
        const result = window.klaviyo.identify(properties);
        if (result && typeof result.catch === 'function') result.catch(() => queueLegacyIdentify(properties));
        return;
      }
    } catch {
      // Fall through to the legacy queue.
    }
    queueLegacyIdentify(properties);
  }

  function queueRescueMetric(name, formId) {
    const properties = { form_id: formId, source: 'LadyBoss rescue popup' };
    try {
      if (typeof window.klaviyo?.push === 'function') {
        window.klaviyo.push(['track', name, properties]);
        return true;
      }
      window._learnq = window._learnq || [];
      window._learnq.push(['track', name, properties]);
      return true;
    } catch {
      return false;
    }
  }

  function syncRescueProfileProperties(state = readRescueState()) {
    const properties = {};
    if (state.permanentDismissed) properties[RESCUE_PROFILE_PROPERTIES.permanentDismissal] = true;
    if (state.completed) properties[RESCUE_PROFILE_PROPERTIES.completed] = true;
    if (Object.keys(properties).length) identifyProfile(properties);

    let changed = false;
    if (state.permanentDismissed && !state.permanentDismissalMetricQueued) {
      state.permanentDismissalMetricQueued = queueRescueMetric(
        RESCUE_METRICS.permanentDismissal,
        state.lastFormId,
      );
      changed ||= state.permanentDismissalMetricQueued;
    }
    if (state.completed && !state.completedMetricQueued) {
      state.completedMetricQueued = queueRescueMetric(RESCUE_METRICS.completed, state.lastFormId);
      changed ||= state.completedMetricQueued;
    }
    if (changed) writeRescueState(state);
  }

  function normalTriggerReady() {
    return runtime.elapsed && runtime.scrollQualified;
  }

  function engagementReady(state) {
    if (Number(state.dismissals || 0) === 0) return true;
    return runtime.freshEngagement &&
      runtime.freshEngagementAt >= Number(state.suppressedUntil || 0) &&
      runtime.freshEngagementAt > Number(state.lastCloseAt || 0);
  }

  function eligibilityReady(state) {
    return (
      !isExcludedVisit() &&
      !welcomeSuppressionActive(state) &&
      !rescueSuppressionActive(readRescueState()) &&
      !rescueVisitHandled() &&
      runtime.cartConfirmedEmpty &&
      engagementReady(state) &&
      (runtime.exitSignal || normalTriggerReady())
    );
  }

  function clearWelcomeFallback() {
    if (runtime.welcomeFallbackTimer === null) return;
    window.clearTimeout(runtime.welcomeFallbackTimer);
    runtime.welcomeFallbackTimer = null;
  }

  function queueRetry() {
    if (runtime.retryCount >= 20 || runtime.opened || runtime.opening || rescueVisitHandled()) return;
    runtime.retryCount += 1;
    window.setTimeout(attemptOpen, 500);
  }

  // The coordinator has already checked the recognized profile and prepared the
  // complete UI. This hook is synchronous: failure preserves the welcome path.
  function trySpinnerReplacement() {
    const replacement = window.__ladybossSpinnerReplacement;
    if ((!replacement?.ready && !replacement?.handled) || typeof replacement.tryOpen !== 'function') return false;
    if (runtime.opened || runtime.opening || runtime.activeFormId || rescueVisitHandled() ||
        rescueSuppressionActive(readRescueState()) || isExcludedSpinnerVisit() ||
        !runtime.cartConfirmedEmpty || hasVisibleDialog()) return false;
    if (replacement.handled) {
      clearWelcomeFallback();
      runtime.opened = true;
      return true;
    }
    if (!acquireLock('ladyboss-free-sunglasses')) return false;
    let mounted = false;
    try {
      mounted = replacement.tryOpen({
        onClose: () => {
          runtime.activeFormId = null;
          runtime.opening = false;
          // A spinner impression owns this visit. It never adds a welcome
          // dismissal, completion, cooldown or profile property.
          runtime.opened = true;
          releaseLock();
        },
      }) === true;
    } catch {
      mounted = false;
    }
    if (!mounted) {
      releaseLock();
      return false;
    }
    clearWelcomeFallback();
    runtime.opened = true;
    runtime.opening = false;
    runtime.activeFormId = 'ladyboss-free-sunglasses';
    return true;
  }

  async function freshWelcomeCartIsEmpty() {
    let timeout;
    try {
      return await Promise.race([
        (async () => {
          const response = await window.fetch('/cart.js', {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) return false;
          const cart = await response.json();
          return Number(cart.item_count) === 0 && Number(cart.total_price) === 0;
        })(),
        new Promise(resolve => { timeout = window.setTimeout(() => resolve(false), 4_000); }),
      ]);
    } catch {
      return false;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function ensureWelcomeOverlayStyle() {
    const id = 'ladyboss-welcome-chat-style';
    if (!document.head || document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = 'body:has(form[data-testid="klaviyo-form-SDDjsT"]) shopify-chat,' +
      'body:has(form[data-testid="klaviyo-form-SG2jCj"]) shopify-chat' +
      '{visibility:hidden!important;pointer-events:none!important}';
    document.head.appendChild(style);
  }

  async function openWelcomeNow() {
    runtime.welcomeFallbackTimer = null;
    if (runtime.opened || runtime.opening || runtime.welcomeCartCheck || rescueVisitHandled()) return;
    const state = readWelcomeState();
    if (!eligibilityReady(state)) return;
    if (runtime.activeFormId || hasVisibleDialog()) return;
    if (trySpinnerReplacement()) return;
    if (welcomeAudienceBlocked()) return;
    if (!klaviyoConfirmed()) {
      queueRetry();
      return;
    }

    // The spinner decision above keeps its original synchronous path. This
    // independent fresh read protects only welcome from a stale empty cart.
    runtime.welcomeCartCheck = true;
    const cartRevision = runtime.welcomeCartRevision;
    const cartIsEmpty = await freshWelcomeCartIsEmpty();
    runtime.welcomeCartCheck = false;
    if (!cartIsEmpty || cartRevision !== runtime.welcomeCartRevision) return;
    // Identity, another tab, a rescue form, or a dialog may have changed while
    // the cart request was pending. Commit only against the current state.
    if (runtime.opened || runtime.opening || !eligibilityReady(readWelcomeState())) return;
    if (runtime.activeFormId || hasVisibleDialog() || welcomeAudienceBlocked()) return;

    const formId = currentWelcomeFormId();
    if (!acquireLock(formId)) return;
    if (!commitWelcomeVisit()) {
      releaseLock();
      return;
    }
    try { window.__ladybossSpinnerReplacement?.primaryCommitted?.(); } catch {}
    ensureWelcomeOverlayStyle();
    runtime.opening = true;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push([
      'openForm',
      formId,
      () => {
        runtime.opened = true;
        runtime.opening = false;
        runtime.activeFormId = formId;
      },
    ]);
    window.setTimeout(() => {
      if (!runtime.opened) {
        runtime.opening = false;
        releaseLock();
      }
    }, 8_000);
  }

  function attemptOpen() {
    if (runtime.opened || runtime.opening || runtime.welcomeCartCheck || runtime.welcomeFallbackTimer !== null || rescueVisitHandled()) return;
    const state = readWelcomeState();
    if (!eligibilityReady(state)) return;

    // Rescue targeting uses the existing time-and-scroll threshold, not exit intent.
    // Preserve the ordinary welcome popup's immediate early-exit reach. At the
    // normal threshold, give native rescue targeting a short first opportunity.
    if (!previewMode && runtime.exitSignal && !normalTriggerReady()) {
      openWelcomeNow();
      return;
    }
    if (!previewMode) {
      runtime.welcomeFallbackTimer = window.setTimeout(openWelcomeNow, RESCUE_HEADSTART_MS);
      return;
    }
    openWelcomeNow();
  }

  function qualifyScroll() {
    const rule = RULES[currentDevice()];
    const scrollable = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const threshold = Math.min(rule.scrollCapPx, scrollable * (rule.scrollPercent / 100));
    if (threshold > 0 && window.scrollY >= threshold) runtime.scrollQualified = true;
  }

  function handleScroll() {
    const now = performance.now();
    const y = window.scrollY;

    qualifyScroll();
    const mayEngage = !runtime.activeFormId && !welcomeSuppressionActive(readWelcomeState());
    if (mayEngage && Math.abs(y - runtime.lastScrollY) >= 24) {
      runtime.freshEngagement = true;
      runtime.freshEngagementAt = Date.now();
    }

    if (currentDevice() === 'mobile') {
      if (y >= runtime.mobilePeakY || now - runtime.mobilePeakAt > 3_000) {
        runtime.mobilePeakY = y;
        runtime.mobilePeakAt = now;
      } else if (mayEngage && runtime.mobilePeakY >= 120 && runtime.mobilePeakY - y >= 100 && now - runtime.mobilePeakAt <= 2_000) {
        runtime.exitSignal = true;
        runtime.freshEngagement = true;
        runtime.freshEngagementAt = Date.now();
      }
    }

    runtime.lastScrollY = y;
    attemptOpen();
  }

  function handlePointerExit(event) {
    if (currentDevice() !== 'desktop') return;
    if (event.relatedTarget || event.toElement || event.clientY > 0) return;
    if (runtime.activeFormId || welcomeSuppressionActive(readWelcomeState())) return;
    runtime.exitSignal = true;
    runtime.freshEngagement = true;
    runtime.freshEngagementAt = Date.now();
    attemptOpen();
  }

  function updateWelcomeStateForSubmission(detail) {
    const state = readWelcomeState();
    const metadata = detail.metaData || detail.metadata || {};
    const stepName = String(metadata.stepName || detail.stepName || '').toLowerCase();
    const emailSubmitted = Boolean(metadata.email) || stepName.includes('email');
    const phoneSubmitted = Boolean(metadata.phone_number) || /sms|mobile|phone/.test(stepName);

    if (emailSubmitted) state.emailCaptured = true;
    if (phoneSubmitted) {
      state.completed = true;
      state.permanent = true;
      state.suppressedUntil = PERMANENT_UNTIL;
    }
    state.lastFormId = detail.formId || detail.formID || currentWelcomeFormId();
    writeWelcomeState(state);
  }

  function updateWelcomeStateForClose(formId) {
    if (runtime.activeFormId !== formId) return;
    const state = readWelcomeState();
    if (!state.completed) {
      const dismissals = Math.min(5, Number(state.dismissals || 0) + 1);
      const step = Math.min(5, Number(state.dismissalStep || 0) + 1);
      const cooldown = DISMISSAL_COOLDOWNS[step - 1];
      state.dismissals = dismissals;
      state.dismissalStep = step;
      state.dismissalsAtStep = dismissals;
      state.lastCloseAt = Date.now();
      state.lastFormId = formId;
      state.permanent = step >= 5;
      state.suppressedUntil = state.permanent ? PERMANENT_UNTIL : Date.now() + cooldown;
      state.lastPath = window.location.pathname;
      writeWelcomeState(state);
    }
    runtime.activeFormId = null;
    runtime.opened = false;
    runtime.opening = false;
    runtime.freshEngagement = false;
    runtime.freshEngagementAt = 0;
    runtime.exitSignal = false;
    runtime.lastScrollY = window.scrollY;
    runtime.mobilePeakY = window.scrollY;
    runtime.mobilePeakAt = performance.now();
    runtime.welcomeCartRevision += 1;
    clearWelcomeFallback();
    releaseLock();
  }

  function updateRescueStateForSubmission(formId) {
    const state = readRescueState();
    state.completed = true;
    state.completedMetricQueued = queueRescueMetric(RESCUE_METRICS.completed, formId);
    state.lastFormId = formId;
    state.permanent = true;
    state.suppressedUntil = PERMANENT_UNTIL;
    writeRescueState(state);
    runtime.rescueSeenOnVisit = true;
    writeRescueVisitState({ opened: true, submitted: true });
    identifyProfile({ [RESCUE_PROFILE_PROPERTIES.completed]: true });
    clearWelcomeFallback();
  }

  function updateRescueStateForClose(formId) {
    const state = readRescueState();
    if (!state.completed && !state.permanent) {
      const dismissals = Math.min(2, Number(state.dismissals || 0) + 1);
      state.dismissals = dismissals;
      state.lastCloseAt = Date.now();
      state.lastFormId = formId;
      state.permanent = dismissals >= 2;
      state.permanentDismissed = state.permanent;
      state.suppressedUntil = state.permanent ? PERMANENT_UNTIL : Date.now() + RESCUE_COOLDOWN_MS;
      writeRescueState(state);
      if (state.permanentDismissed) {
        state.permanentDismissalMetricQueued = queueRescueMetric(RESCUE_METRICS.permanentDismissal, formId);
        writeRescueState(state);
        identifyProfile({ [RESCUE_PROFILE_PROPERTIES.permanentDismissal]: true });
      }
    }
    runtime.rescueSeenOnVisit = true;
    writeRescueVisitState({ closed: true, opened: true });
    runtime.activeFormId = null;
    runtime.opened = false;
    runtime.opening = false;
    clearWelcomeFallback();
    releaseLock();
  }

  function handleFormEvent(event) {
    const detail = event.detail || {};
    const formId = detail.formId || detail.formID;
    const type = detail.type;
    if (!formId) return;

    if (type === 'open') {
      clearWelcomeFallback();
      runtime.activeFormId = formId;
      if (RESCUE_FORM_IDS.has(formId)) {
        try { window.__ladybossSpinnerReplacement?.yieldToRescue?.(); } catch {}
        runtime.activeFormId = formId;
        runtime.rescueSeenOnVisit = true;
        runtime.opened = false;
        runtime.opening = false;
        writeRescueVisitState({ opened: true });
        releaseLock();
      } else if (WELCOME_FORM_IDS.has(formId)) {
        commitWelcomeVisit();
        try { window.__ladybossSpinnerReplacement?.primaryCommitted?.(); } catch {}
        runtime.opened = true;
        runtime.opening = false;
      }
      return;
    }

    if (type === 'close') {
      if (RESCUE_FORM_IDS.has(formId)) updateRescueStateForClose(formId);
      else if (WELCOME_FORM_IDS.has(formId)) updateWelcomeStateForClose(formId);
      else if (runtime.activeFormId === formId) runtime.activeFormId = null;
      return;
    }

    if (type === 'stepSubmit' || type === 'submit') {
      if (RESCUE_FORM_IDS.has(formId)) updateRescueStateForSubmission(formId);
      else if (WELCOME_FORM_IDS.has(formId)) updateWelcomeStateForSubmission(detail);
    }
  }

  async function confirmEmptyCart() {
    try {
      const response = await window.fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      runtime.cartConfirmedEmpty = Number(cart.item_count || 0) === 0 && Number(cart.total_price || 0) === 0;
      attemptOpen();
    } catch {
      runtime.cartConfirmedEmpty = false;
    }
  }

  function initializeWelcome() {
    if (runtime.welcomeListenersStarted || rescueVisitHandled()) return;
    const state = readWelcomeState();
    if (state.completed || state.permanent) return;
    if (Number(state.suppressedUntil || 0) > Date.now()) {
      const wait = Math.min(Number(state.suppressedUntil) - Date.now() + 50, 2_147_000_000);
      window.setTimeout(initializeWelcome, wait);
      return;
    }

    if (previewMode) {
      runtime.elapsed = true;
      runtime.scrollQualified = true;
      runtime.freshEngagement = true;
      runtime.freshEngagementAt = Date.now();
    }

    const previousPath = state.lastPath;
    if (!previewMode) {
      runtime.freshEngagement = Boolean(
        state.lastCloseAt && previousPath !== null && previousPath !== window.location.pathname &&
        runtime.pageStartedAt >= Number(state.suppressedUntil || 0),
      );
      runtime.freshEngagementAt = runtime.freshEngagement ? runtime.pageStartedAt : 0;
    }
    state.lastPath = window.location.pathname;
    if (!writeWelcomeState(state)) return;

    runtime.welcomeListenersStarted = true;
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.documentElement.addEventListener('mouseout', handlePointerExit);
    // These listeners invalidate only an in-flight welcome check; they do
    // not change the shared cart cache or any spinner behavior.
    for (const type of ['cart:change', 'variant:add', 'pagehide']) {
      window.addEventListener(type, () => { runtime.welcomeCartRevision += 1; });
    }

    if (!previewMode) {
      const rule = RULES[currentDevice()];
      window.setTimeout(() => {
        runtime.elapsed = true;
        qualifyScroll();
        attemptOpen();
      }, rule.delayMs);
    }

    qualifyScroll();
    void confirmEmptyCart();
    if (previewMode) attemptOpen();
  }

  function initialize() {
    if (runtime.initialized || (!PUBLIC_ENABLED && !previewMode) || !storageWorks() || isExcludedVisit()) return;
    runtime.initialized = true;

    if (previewMode) {
      try {
        window.localStorage.removeItem(WELCOME_STORAGE_KEY);
        window.localStorage.removeItem(LOCK_KEY);
      } catch {
        return;
      }
      if (previewResetOnly) return;
    }

    const rescueVisit = readRescueVisitState();
    runtime.rescueSeenOnVisit = rescueVisit.opened || rescueVisit.closed || rescueVisit.submitted;
    window.addEventListener('klaviyoForms', handleFormEvent);
    window.addEventListener('pagehide', releaseLock, { once: true });
    syncRescueProfileProperties();
    initializeWelcome();
    if (window.__ladybossSpinnerReplacement?.ready) void confirmEmptyCart();
  }

  window.__ladybossPopupReplacementBridge = Object.freeze({
    prepareSpinner: () => {
      if (isExcludedSpinnerVisit() || (!PUBLIC_ENABLED && !previewMode) || !storageWorks()) return;
      if (!runtime.initialized) {
        // Owned-channel arrivals retain their existing welcome exclusion.
        // A verified spinner may still use the shared rescue/cart/lock guards.
        if (!isExcludedVisit()) return;
        runtime.initialized = true;
        const visit = readRescueVisitState();
        runtime.rescueSeenOnVisit = visit.opened || visit.closed || visit.submitted;
        window.addEventListener('klaviyoForms', handleFormEvent);
        window.addEventListener('pagehide', releaseLock, { once: true });
      }
      if (!rescueVisitHandled()) void confirmEmptyCart();
    },
    attemptSpinner: trySpinnerReplacement,
    attemptWelcome: attemptOpen,
  });
  window.dispatchEvent(new CustomEvent('ladyboss:popup-controller-ready'));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
